import * as React from 'react';
import * as moment from 'moment';
import { Modal, FormControl, FormGroup, Col, ControlLabel, Alert, HelpBlock, Row, Button } from 'react-bootstrap';
import { IListItem, IMatchOfferDeclineReasonListItem, IMatchOfferDeclineRootCause, IRecipient, IDonor, IMatchOfferDeclineChoices } from '@nkr/models/index';
import { MatchOfferDeclineRootCause } from '@nkr/enums/match-offer-decline-root-cause';
import { MatchOfferRejectionReason } from '@nkr/enums/match-offer-rejection-reason';
import { MatchOfferDeclineReason } from '@nkr/enums/match-offer-decline-reason';
import { MatchOfferDeclineGroup } from '@nkr/enums/index';
import { PatientType } from '@nkr/enums/index'
import { MatchDecisionBodyWrapper } from '../styles';

interface IProps {
    show: boolean,
    accept: boolean,
    kidneyArrivalDateTime: string,
    isThisAnAboTransplant: boolean,
    isPastDeadline: boolean,
    recipient: IRecipient,
    donor: IDonor,
    isClc: boolean,
    isStreamlinedContract: boolean,
    currentDeclineRatio: number,
    newDeclineRatio: number,
    showCheckKidney: boolean,
    declineRootCauseList: IMatchOfferDeclineRootCause[],
    rejectionReasonList: IListItem[],
    declineReasonList: IMatchOfferDeclineReasonListItem[],
    onSubmitAccept: (isFullyConfident: boolean, whichKideny: string, lateReason: string, additionalComments: string, initials: string) => void,
    onSubmitDecline: (blockFutureMatches: boolean, deactivatePair: boolean, rejectionReason: number, rejectionReasonText: string, additionalComments: string, initials: string, matchOfferDeclineChoices: IMatchOfferDeclineChoices) => void,
    onCancel: () => void
}

interface IState {
    isFullyConfident: boolean,
    whichKidney: string,
    deactivatePair: boolean,
    lateReason: string,
    rejectionReason: number,
    rejectionReasonText: string,
    measuresToTake: string,
    additionalComments: string,

    initials: string,

    isDateIssue: boolean,
    isReasonOther: boolean,

    acceptValidation: {
        isFullyConfident: boolean,
        whichKidney: boolean,
        initials: boolean
    },
    declineValidation: {
        initials: boolean,
        isDonorAcceptable: boolean,
        otherReasonDonorBad: boolean,
        otherReasonPhs: boolean,
        nextAvailTargetSurgeryDate: boolean,
        isSurgeryDateAcceptable: boolean,
        otherReasonSurgeryDateBad: boolean,
        deactivatePatient: boolean,
        otherReasonForPatientDeactivation: boolean,
        donorUnacceptableReasons: boolean,
        reasonsTargeySurgeryUnacceptable: boolean,
        patientDeactivatedReasons: boolean,
    },
    isDonorAcceptable: boolean,
    otherReasonDonorBad: string,
    otherReasonPhs: string,
    nextAvailTargetSurgeryDate: string,
    isSurgeryDateAcceptable: boolean,
    otherReasonSurgeryDateBad: string,
    deactivatePatient: boolean,
    otherReasonForPatientDeactivation: string,
    donorUnacceptableReasons: MatchOfferDeclineReason[],
    reasonsTargeySurgeryUnacceptable: MatchOfferDeclineReason[],
    patientDeactivatedReasons: MatchOfferDeclineReason[],
    dirty: boolean
}

const initialState: IState = {
    isFullyConfident: true,
    whichKidney: null,
    deactivatePair: null,
    lateReason: '',
    rejectionReason: 0,
    rejectionReasonText: '',
    measuresToTake: '',
    additionalComments: '',
    initials: '',
    isDateIssue: false,
    isReasonOther: false,
    acceptValidation: {
        isFullyConfident: true,
        whichKidney: true,
        initials: true
    },
    declineValidation: {
        initials: true,
        isDonorAcceptable: true,
        otherReasonDonorBad: true,
        otherReasonPhs: true,
        nextAvailTargetSurgeryDate: true,
        isSurgeryDateAcceptable: true,
        otherReasonSurgeryDateBad: true,
        deactivatePatient: true,
        otherReasonForPatientDeactivation: true,
        donorUnacceptableReasons: true,
        reasonsTargeySurgeryUnacceptable: true,
        patientDeactivatedReasons: true,
    },
    isDonorAcceptable: null,
    otherReasonDonorBad: '',
    otherReasonPhs: '',
    nextAvailTargetSurgeryDate: '',
    isSurgeryDateAcceptable: null,
    otherReasonSurgeryDateBad: '',
    deactivatePatient: null,
    otherReasonForPatientDeactivation: '',
    donorUnacceptableReasons: [],
    reasonsTargeySurgeryUnacceptable: [],
    patientDeactivatedReasons: [],
    dirty: false
}

class MatchOfferDecision extends React.Component<IProps, IState> {
    readonly state: IState = initialState;

    componentWillReceiveProps = (nextProps: IProps) => {
        if (nextProps.show) {
            this.setState(initialState);
        }
    }

    handleFullyConfidentChange = (isFullyConfident: boolean) => {
        this.setState({
            isFullyConfident
        });
    }

    handleLateReasonChange = (e: React.FormEvent<FormControl & HTMLTextAreaElement>) => {
        this.setState({
            lateReason: e.currentTarget.value
        });
    }

    handleUnusualIssuesChange = (e: React.FormEvent<FormControl & HTMLTextAreaElement>) => {
        this.setState({
            additionalComments: e.currentTarget.value
        });
    }

    handleDonorIsAcceptable = (isDonorAcceptable: boolean) => {
        this.setState({
            isDonorAcceptable
        })
    }

    handleIsSurgeryDateAcceptable = (isSurgeryDateAcceptable: boolean) => {
        this.setState({
            isSurgeryDateAcceptable
        })
    }

    handleDeactivatePairChange = (deactivatePair: boolean) => {
        this.setState({
            deactivatePair
        });
    }

    handleWhichKidneyChange = (whichKidney: string) => {
        this.setState({
            whichKidney
        })
    }

    handleRejectionReasonChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        let rejectionReason = parseInt(e.currentTarget.value);
        let deactivatePair = [MatchOfferDeclineRootCause.PatientNotReady, MatchOfferDeclineRootCause.CadavericKidney, MatchOfferDeclineRootCause.SurgicalCapacity, MatchOfferDeclineRootCause.Other, MatchOfferDeclineRootCause.RecipientSurgeon].indexOf(rejectionReason) >= 0;

        let isDateIssue = rejectionReason == MatchOfferRejectionReason.CannotAccommodateSurgeryDate;
        let isDonorNotAcceptable = rejectionReason == MatchOfferRejectionReason.DonorNotAcceptable;
        let isReasonOther = isDateIssue || isDonorNotAcceptable;

        this.setState({
            rejectionReason,
            deactivatePair,
            isDateIssue,
            isReasonOther
        });
    }

    handleRejectReasonTextChange = (e: React.FormEvent<FormControl & HTMLTextAreaElement>) => {
        this.setState({
            rejectionReasonText: e.currentTarget.value
        });
    }

    handleInitialsChange = (e: React.FormEvent<HTMLInputElement>) => {
        this.setState({
            initials: e.currentTarget.value
        });
    }

    handleSubmit = () => {
        if (this.props.accept) {
            let acceptValidation = this.validateAccept();

            this.setState({
                acceptValidation,
                dirty: true
            }, () => {
                if (this.isValid()) {
                    let { isFullyConfident, whichKidney, lateReason, additionalComments, initials } = this.state;
                    this.props.onSubmitAccept(isFullyConfident, whichKidney, lateReason, additionalComments, initials);
                }
            });
        }

        if (!this.props.accept) {
            let declineValidation = this.validateDecline();

            this.setState({
                declineValidation,
                dirty: true
            }, () => {
                if (this.isValid()) {
                    let { deactivatePair, rejectionReason, rejectionReasonText, additionalComments, initials, isDonorAcceptable } = this.state;

                    let matchOfferdeclineChoices = this.handleDeclineQuestionProperties()

                    this.props.onSubmitDecline(isDonorAcceptable, deactivatePair, rejectionReason, rejectionReasonText, additionalComments, initials, matchOfferdeclineChoices);
                }
            });
        }
    }

    getRootList = () => {
        if (this.state.isDateIssue) {
            return this.props.declineRootCauseList.filter(x => x.isDateIssue);
        }

        return this.props.declineRootCauseList.filter(x => x.isDateIssue == false && x.isNonDateIssue == true);
    }

    validateAccept = () => {
        let { isFullyConfident, whichKidney, initials, acceptValidation } = this.state;

        acceptValidation.isFullyConfident = isFullyConfident != null;
        acceptValidation.whichKidney = whichKidney || !this.props.showCheckKidney ? true : false;
        acceptValidation.initials = initials != '';

        return acceptValidation;
    }

    validateDecline = () => {
        let { initials, declineValidation } = this.state;
        let { isDonorAcceptable, otherReasonDonorBad, isSurgeryDateAcceptable, otherReasonSurgeryDateBad, nextAvailTargetSurgeryDate, deactivatePatient, otherReasonForPatientDeactivation, donorUnacceptableReasons, reasonsTargeySurgeryUnacceptable, patientDeactivatedReasons } = this.state;

        declineValidation.initials = initials != '';

        declineValidation.isDonorAcceptable = isDonorAcceptable != null;
        declineValidation.donorUnacceptableReasons = isDonorAcceptable != false || (isDonorAcceptable == false && donorUnacceptableReasons.length > 0);
        declineValidation.otherReasonDonorBad = isDonorAcceptable != false
            || (isDonorAcceptable == false && !donorUnacceptableReasons.includes(MatchOfferDeclineReason.DonorOther))
            || (isDonorAcceptable == false && donorUnacceptableReasons.includes(MatchOfferDeclineReason.DonorOther) && otherReasonDonorBad != '');
        declineValidation.otherReasonPhs = isDonorAcceptable != false
            || !donorUnacceptableReasons.includes(MatchOfferDeclineReason.PublicHealthServicePHSIncreasedRisk)
            || this.state.otherReasonPhs.trim() != '';

        declineValidation.isSurgeryDateAcceptable = isDonorAcceptable != true
            || (isDonorAcceptable == true && isSurgeryDateAcceptable != null);

        declineValidation.reasonsTargeySurgeryUnacceptable = isDonorAcceptable != true
            || (isDonorAcceptable == true && isSurgeryDateAcceptable == true)
            || (isDonorAcceptable == true && isSurgeryDateAcceptable == false && reasonsTargeySurgeryUnacceptable.length > 0);

        declineValidation.otherReasonSurgeryDateBad = isDonorAcceptable != true
            || (isDonorAcceptable == true && isSurgeryDateAcceptable == true)
            || (isDonorAcceptable == true && isSurgeryDateAcceptable == false && !reasonsTargeySurgeryUnacceptable.includes(MatchOfferDeclineReason.SurgeryDateOther))
            || (isDonorAcceptable == true && isSurgeryDateAcceptable == false && otherReasonSurgeryDateBad != '' && reasonsTargeySurgeryUnacceptable.includes(MatchOfferDeclineReason.SurgeryDateOther));

        declineValidation.nextAvailTargetSurgeryDate = isDonorAcceptable != true
            || (isDonorAcceptable == true && isSurgeryDateAcceptable == true)
            || (isDonorAcceptable == true && isSurgeryDateAcceptable == false && ![MatchOfferDeclineReason.OperatingRoomNotAvailable, MatchOfferDeclineReason.DonorSurgeonNotAvailable, MatchOfferDeclineReason.RecipientSurgeonNotAvailable].some(reason =>
                reasonsTargeySurgeryUnacceptable.includes(reason)
            ))
            || (isDonorAcceptable == true && isSurgeryDateAcceptable == false && nextAvailTargetSurgeryDate != '' && [MatchOfferDeclineReason.OperatingRoomNotAvailable, MatchOfferDeclineReason.DonorSurgeonNotAvailable, MatchOfferDeclineReason.RecipientSurgeonNotAvailable].some(reason =>
                reasonsTargeySurgeryUnacceptable.includes(reason)
            ));

        let targetReasonsContainPaired = reasonsTargeySurgeryUnacceptable.includes(MatchOfferDeclineReason.PairedDonorNotAvailable) || reasonsTargeySurgeryUnacceptable.includes(MatchOfferDeclineReason.PatientNotAvailable);

        declineValidation.deactivatePatient = isDonorAcceptable != true
            || (isSurgeryDateAcceptable == null)
            || (isDonorAcceptable == true && isSurgeryDateAcceptable == true && deactivatePatient != null)
            || (isDonorAcceptable == true && isSurgeryDateAcceptable == true && reasonsTargeySurgeryUnacceptable.length > 0 && targetReasonsContainPaired && deactivatePatient != null)
            || (isDonorAcceptable == true && isSurgeryDateAcceptable == false && reasonsTargeySurgeryUnacceptable.length > 0 && !targetReasonsContainPaired)
            || (isDonorAcceptable == true && isSurgeryDateAcceptable == false && reasonsTargeySurgeryUnacceptable.length > 0 && targetReasonsContainPaired && deactivatePatient != null);

        declineValidation.patientDeactivatedReasons = isDonorAcceptable == false
            || (isDonorAcceptable == true && isSurgeryDateAcceptable == true && deactivatePatient == false)
            || (isDonorAcceptable == true && isSurgeryDateAcceptable == true && deactivatePatient == true && patientDeactivatedReasons.length > 0)
            || (isDonorAcceptable == true && isSurgeryDateAcceptable == false && reasonsTargeySurgeryUnacceptable.length > 0 && !targetReasonsContainPaired)
            || (isDonorAcceptable == true && isSurgeryDateAcceptable == false && reasonsTargeySurgeryUnacceptable.length > 0 && targetReasonsContainPaired && deactivatePatient == false)
            || (isDonorAcceptable == true && isSurgeryDateAcceptable == false && reasonsTargeySurgeryUnacceptable.length > 0 && targetReasonsContainPaired && deactivatePatient == true)
            || (isDonorAcceptable == true && isSurgeryDateAcceptable == false && reasonsTargeySurgeryUnacceptable.length > 0 && targetReasonsContainPaired && deactivatePatient == true && patientDeactivatedReasons.length > 0)

        declineValidation.otherReasonForPatientDeactivation = isDonorAcceptable == false
            || (isDonorAcceptable == true && isSurgeryDateAcceptable == true && deactivatePatient == false)
            || (isDonorAcceptable == true && isSurgeryDateAcceptable == true && deactivatePatient == true && patientDeactivatedReasons.length > 0 && (!patientDeactivatedReasons.includes(MatchOfferDeclineReason.PatientDeactivateOther) || patientDeactivatedReasons.includes(MatchOfferDeclineReason.PatientDeactivateOther) && otherReasonForPatientDeactivation != ''))
            || (isDonorAcceptable == true && isSurgeryDateAcceptable == false && reasonsTargeySurgeryUnacceptable.length > 0 && !targetReasonsContainPaired)
            || (isDonorAcceptable == true && isSurgeryDateAcceptable == false && reasonsTargeySurgeryUnacceptable.length > 0 && targetReasonsContainPaired && deactivatePatient == false)
            || (isDonorAcceptable == true && isSurgeryDateAcceptable == false && reasonsTargeySurgeryUnacceptable.length > 0 && targetReasonsContainPaired && deactivatePatient == false)
            || (isDonorAcceptable == true && isSurgeryDateAcceptable == false && reasonsTargeySurgeryUnacceptable.length > 0 && targetReasonsContainPaired && deactivatePatient == true)
            || (isDonorAcceptable == true && isSurgeryDateAcceptable == false && reasonsTargeySurgeryUnacceptable.length > 0 && targetReasonsContainPaired && deactivatePatient == true && patientDeactivatedReasons.length > 0 && ((patientDeactivatedReasons.includes(MatchOfferDeclineReason.PatientDeactivateOther) && otherReasonForPatientDeactivation != '') || !patientDeactivatedReasons.includes(MatchOfferDeclineReason.PatientDeactivateOther)))

        return declineValidation;
    }

    handleDeclineQuestionProperties = () => {
        let { isDonorAcceptable, otherReasonDonorBad, isSurgeryDateAcceptable, otherReasonSurgeryDateBad, nextAvailTargetSurgeryDate, deactivatePatient, otherReasonForPatientDeactivation, donorUnacceptableReasons, reasonsTargeySurgeryUnacceptable, patientDeactivatedReasons } = this.state;

        let targetReasonsContainPaired = reasonsTargeySurgeryUnacceptable.includes(MatchOfferDeclineReason.PairedDonorNotAvailable) || reasonsTargeySurgeryUnacceptable.includes(MatchOfferDeclineReason.PatientNotAvailable);

        let declineReasons: { id: number, otherText: string, nextAvailText: string }[] = [];

        isDonorAcceptable = isDonorAcceptable;
        donorUnacceptableReasons = isDonorAcceptable == false ? donorUnacceptableReasons : [];
        otherReasonDonorBad = isDonorAcceptable == false && donorUnacceptableReasons.includes(MatchOfferDeclineReason.DonorOther) && otherReasonDonorBad != '' ? otherReasonDonorBad : '';
        const otherReasonPhs = isDonorAcceptable == false && donorUnacceptableReasons.includes(MatchOfferDeclineReason.PublicHealthServicePHSIncreasedRisk) ? this.state.otherReasonPhs.trim() : '';

        isSurgeryDateAcceptable = isDonorAcceptable == true ? isSurgeryDateAcceptable : null;
        reasonsTargeySurgeryUnacceptable = isDonorAcceptable == true && isSurgeryDateAcceptable == false ? reasonsTargeySurgeryUnacceptable : [];
        otherReasonSurgeryDateBad = isDonorAcceptable == true && isSurgeryDateAcceptable == false && reasonsTargeySurgeryUnacceptable.includes(MatchOfferDeclineReason.SurgeryDateOther) && otherReasonSurgeryDateBad != '' ? otherReasonSurgeryDateBad : ''

        nextAvailTargetSurgeryDate = isDonorAcceptable == true && isSurgeryDateAcceptable == false && [MatchOfferDeclineReason.OperatingRoomNotAvailable, MatchOfferDeclineReason.DonorSurgeonNotAvailable, MatchOfferDeclineReason.RecipientSurgeonNotAvailable].some(reason =>
            reasonsTargeySurgeryUnacceptable.includes(reason)) && nextAvailTargetSurgeryDate != '' ? nextAvailTargetSurgeryDate : ''

        deactivatePatient = (isDonorAcceptable == true && isSurgeryDateAcceptable == true) || (isDonorAcceptable == true && isSurgeryDateAcceptable == false && targetReasonsContainPaired) ? deactivatePatient : false;
        patientDeactivatedReasons = ((isDonorAcceptable == true && isSurgeryDateAcceptable == true) || (isDonorAcceptable == true && isSurgeryDateAcceptable == false && targetReasonsContainPaired)) && deactivatePatient == true ? patientDeactivatedReasons : [];
        otherReasonForPatientDeactivation = ((isDonorAcceptable == true && isSurgeryDateAcceptable == true) || (isDonorAcceptable == true && isSurgeryDateAcceptable == false && targetReasonsContainPaired)) && deactivatePatient == true && patientDeactivatedReasons.includes(MatchOfferDeclineReason.PatientDeactivateOther) && otherReasonForPatientDeactivation != '' ? otherReasonForPatientDeactivation : '';

        donorUnacceptableReasons.forEach(x => {
            if (x == MatchOfferDeclineReason.DonorOther) {
                declineReasons.push({ id: x, otherText: otherReasonDonorBad, nextAvailText: '' })
            }
            else if (x == MatchOfferDeclineReason.PublicHealthServicePHSIncreasedRisk) {
                declineReasons.push({ id: x, otherText: otherReasonPhs, nextAvailText: '' })
            }
            else {
                declineReasons.push({ id: x, otherText: null, nextAvailText: null })
            }
        })

        reasonsTargeySurgeryUnacceptable.forEach(x => {
            if (x == MatchOfferDeclineReason.SurgeryDateOther
                || x == MatchOfferDeclineReason.OperatingRoomNotAvailable || x == MatchOfferDeclineReason.DonorSurgeonNotAvailable || x == MatchOfferDeclineReason.RecipientSurgeonNotAvailable
            ) {
                declineReasons.push({ id: x, otherText: otherReasonSurgeryDateBad, nextAvailText: nextAvailTargetSurgeryDate })
            }
            else {
                declineReasons.push({ id: x, otherText: null, nextAvailText: null })
            }
        })

        patientDeactivatedReasons.forEach(x => {
            if (x == MatchOfferDeclineReason.PatientDeactivateOther) {
                declineReasons.push({ id: x, otherText: otherReasonForPatientDeactivation, nextAvailText: '' })
            }
            else {
                declineReasons.push({ id: x, otherText: null, nextAvailText: null })
            }
        })

        let matchOfferDeclineHistory: IMatchOfferDeclineChoices = {
            isDonorAcceptable,
            isSurgeryDateAcceptable,
            deactivatePatient,
            declineReasons
        }

        return matchOfferDeclineHistory;
    }

    isValid = () => {
        let { acceptValidation, declineValidation } = this.state;
        let { isFullyConfident, whichKidney, initials: acceptInitials } = acceptValidation;
        let acceptValid = isFullyConfident && (whichKidney || !this.props.showCheckKidney) && acceptInitials;

        let { initials: declineInitials } = declineValidation;
        let { isDonorAcceptable, otherReasonDonorBad, isSurgeryDateAcceptable, otherReasonSurgeryDateBad, nextAvailTargetSurgeryDate, deactivatePatient, otherReasonForPatientDeactivation, donorUnacceptableReasons, reasonsTargeySurgeryUnacceptable, patientDeactivatedReasons } = declineValidation;

        let declineValid = declineInitials;
        declineValid = declineValid && isDonorAcceptable && otherReasonDonorBad && declineValidation.otherReasonPhs && isSurgeryDateAcceptable && otherReasonSurgeryDateBad && nextAvailTargetSurgeryDate && deactivatePatient && otherReasonForPatientDeactivation && donorUnacceptableReasons && reasonsTargeySurgeryUnacceptable && patientDeactivatedReasons

        return this.props.accept ? acceptValid : declineValid;
    }

    handleDonorUnacceptableCheckboxChange = (reason: MatchOfferDeclineReason) => {
        this.setState(prevState => {
            const { donorUnacceptableReasons } = prevState;

            if (donorUnacceptableReasons.includes(reason)) {
                return { donorUnacceptableReasons: donorUnacceptableReasons.filter(x => x != reason) }
            } else {
                return { donorUnacceptableReasons: [...donorUnacceptableReasons, reason] }
            }
        })
    }

    handleUnacceptableSurgeryCheckboxChange = (reason: MatchOfferDeclineReason) => {
        this.setState(prevState => {
            const { reasonsTargeySurgeryUnacceptable } = prevState;

            const updated = reasonsTargeySurgeryUnacceptable.includes(reason)
                ? reasonsTargeySurgeryUnacceptable.filter(x => x != reason)
                : [...reasonsTargeySurgeryUnacceptable, reason];

            const containsPairedOrPatient = updated.includes(MatchOfferDeclineReason.PairedDonorNotAvailable)
                || updated.includes(MatchOfferDeclineReason.PatientNotAvailable);

            return {
                reasonsTargeySurgeryUnacceptable: updated,
                deactivatePatient: containsPairedOrPatient ? true : prevState.deactivatePatient
            };
        });
    }

    handlePatientDeactivatedCheckboxChange = (reason: MatchOfferDeclineReason) => {
        this.setState(prevState => {
            const { patientDeactivatedReasons } = prevState;

            if (patientDeactivatedReasons.includes(reason)) {
                return { patientDeactivatedReasons: patientDeactivatedReasons.filter(x => x != reason) }
            } else {
                return { patientDeactivatedReasons: [...patientDeactivatedReasons, reason] }
            }
        })
    }

    donorIsAcceptable = () => this.state.isDonorAcceptable == true;

    surgeryDateIsAcceptable = () => this.state.isSurgeryDateAcceptable == true;

    pairDoesntNeedDeactivation = () => this.state.deactivatePatient == false;

    surgeryDateReasonsRequirePatientDeactivationQuestion = () => {
        let pairedDonorNotAvailable = this.state.reasonsTargeySurgeryUnacceptable.includes(MatchOfferDeclineReason.PairedDonorNotAvailable);
        let patientNotAvailable = this.state.reasonsTargeySurgeryUnacceptable.includes(MatchOfferDeclineReason.PatientNotAvailable);

        return pairedDonorNotAvailable || patientNotAvailable;
    }

    render = () => {
        const isValid = this.isValid();

        const chainEndDeclineRatio =
            this.props.recipient.center.declineRatio >= 0 &&
            this.props.recipient.isInChip &&
            this.props.recipient.isAdpChip == false &&
            !(
                this.props.donor.bloodType == "AB" &&
                this.props.recipient.patientTypeId == PatientType.Waitlist
            );

        const isWaitListPatient = this.props.recipient.patientTypeId == PatientType.Waitlist;
        const showDonorUnacceptableReasons = !this.donorIsAcceptable() && this.state.isDonorAcceptable != null;
        const showIsTargetSurgeryDateQuestion = this.donorIsAcceptable();
        const showIsTargetSurgeryDateUnacceptableReasons = showIsTargetSurgeryDateQuestion && !this.surgeryDateIsAcceptable() && this.state.isSurgeryDateAcceptable != null;
        const showNextAvailableTargetSurgeryDates = ![MatchOfferDeclineReason.OperatingRoomNotAvailable, MatchOfferDeclineReason.DonorSurgeonNotAvailable, MatchOfferDeclineReason.RecipientSurgeonNotAvailable].some(reason =>
            this.state.reasonsTargeySurgeryUnacceptable.includes(reason)
        )
        const showPatientNeedToBeDeactivated = this.surgeryDateIsAcceptable() ? (this.surgeryDateIsAcceptable() || (showIsTargetSurgeryDateUnacceptableReasons && this.surgeryDateReasonsRequirePatientDeactivationQuestion())) && this.donorIsAcceptable() : false;
        const showPatientNeedToBeDeactivatedReasons = showPatientNeedToBeDeactivated && this.state.deactivatePatient == true;
        const donorIsUnacceptableReasonList = this.props.declineReasonList.filter(x => x.declineGroup == MatchOfferDeclineGroup.DonorUnacceptable).sort((a, b) => a.id - b.id);
        const targetSurgeryIsUnacceptableReasonList = this.props.declineReasonList.filter(x => x.declineGroup == MatchOfferDeclineGroup.SurgeryDateUnacceptable);
        const patientDeactivationReasonList = this.props.declineReasonList.filter(x => x.declineGroup == MatchOfferDeclineGroup.PatientDeactivated);
        const invalidMatchOfferDecline = this.donorIsAcceptable() && this.surgeryDateIsAcceptable() && this.pairDoesntNeedDeactivation() && this.props.accept == false;
        const pairedReasons = [MatchOfferDeclineReason.PairedDonorNotAvailable, MatchOfferDeclineReason.PatientNotAvailable];
        const patientDeactivateQuestionLabel = this.donorIsAcceptable() && (this.surgeryDateIsAcceptable() || (!this.surgeryDateIsAcceptable() && this.surgeryDateReasonsRequirePatientDeactivationQuestion()))
            ? '3. Does the patient need to be deactivated?'
            : '2. Does the patient need to be deactivated?';

        const termsLink = this.props.isStreamlinedContract
            ? 'https://www.kidneyregistry.com/membership-terms'
            : 'https://www.kidneyregistry.com/membership-terms-legacy';

        return (
            <Modal bsSize='lg' show={this.props.show} backdrop='static' onHide={this.props.onCancel}>
                <Modal.Header closeButton>
                    <Modal.Title>{this.props.accept ? 'Accept Offer' : 'Decline Offer'}</Modal.Title>
                </Modal.Header>
                <MatchDecisionBodyWrapper>
                    {this.props.accept == false && chainEndDeclineRatio && (
                        <Row>
                            <Col xs={12}>
                                <Alert bsStyle='warning' bsSize='sm'>
                                    <div style={{ marginBottom: 10 }}><strong>Please be aware of the following:</strong></div>
                                    <ul style={{ paddingLeft: 20 }}>
                                        <li>The cutoff ratio for chain end declines is 30%</li>
                                        <li>Your current decline ratio is {this.props.currentDeclineRatio}%</li>
                                        <li>Declining this match offer will put your decline ratio at {this.props.newDeclineRatio}%</li>
                                    </ul>
                                </Alert>
                            </Col>
                        </Row>
                    )}

                    {this.state.dirty && !isValid && (
                        <Row>
                            <Col xs={12}>
                                <Alert bsStyle='danger' bsSize='sm'>
                                    The items in red are required and must be completed prior to submitting your decision.
                                </Alert>
                            </Col>
                        </Row>
                    )}

                    {this.props.recipient.hasMortalityRisk && !this.props.accept && !isWaitListPatient && (
                        <Row>
                            <Col xs={12}>
                                <Alert bsStyle='danger' bsSize='sm'>
                                    By declining this potential donor for your patient, it is possible the patient may not receive a match offer for some time.
                                    <br /><span> The one year mortality risk for this patient is <strong>{this.props.recipient.mortalityRiskPercent}</strong>{`%`}</span>
                                </Alert>
                            </Col>
                        </Row>
                    )}

                    <div>
                        {this.props.accept ?
                            <>
                                {this.props.kidneyArrivalDateTime != '' && (
                                    <div className='row mo_accepted' style={{ marginBottom: 15 }}>
                                        <Col xs={12} lg={6}>
                                            <strong>Scheduled Surgery Date:</strong>
                                        </Col>
                                        <Col xs={12} lg={6}>
                                            {moment(this.props.kidneyArrivalDateTime).format('MM/DD/YYYY')}
                                        </Col>
                                    </div>
                                )}

                                {this.props.showCheckKidney && (
                                    <div className='row mo_accepted'>
                                        <FormGroup validationState={this.state.dirty && this.state.acceptValidation.whichKidney == false ? 'error' : null}>
                                            <Col lg={6} componentClass={ControlLabel}>
                                                The donor center indicated both kidneys are available, please select which kidney is preferred:
                                            </Col>
                                            <Col lg={6}>
                                                <label style={{ marginRight: 15, fontWeight: 'normal' }}>
                                                    <input
                                                        name='whichKideny'
                                                        type='radio'
                                                        style={{ marginRight: 5 }}
                                                        checked={this.state.whichKidney != null && this.state.whichKidney == 'L-opt'}
                                                        onClick={() => this.handleWhichKidneyChange('L-opt')} /> Left
                                                </label>
                                                <label style={{ fontWeight: 'normal' }}>
                                                    <input
                                                        name='whichKideny'
                                                        type='radio'
                                                        style={{ marginRight: 5 }}
                                                        checked={this.state.whichKidney != null && this.state.whichKidney == 'R-opt'}
                                                        onClick={() => this.handleWhichKidneyChange('R-opt')} /> Right
                                                </label>
                                            </Col>
                                        </FormGroup>
                                    </div>
                                )}

                                <div className='row'>
                                    <Col xs={12} lg={12}>
                                        <div className='text-center' style={{ marginTop: 10, backgroundColor: '#f5f5f5', paddingTop: 3, paddingBottom: 3 }}>
                                            By accepting this match, your center agrees to abide by the
                                            current <a style={{ color: 'blue' }} href={termsLink} target='_blank'>member
                                                center terms &amp; conditions</a> as posted on the NKR website.
                                        </div>
                                    </Col>
                                </div>
                            </>
                            :
                            <>
                                <div className="row">
                                    <FormGroup validationState={this.state.declineValidation.isDonorAcceptable ? null : "error"}>
                                        <Col lg={6} componentClass={ControlLabel}>
                                            1. Is the donor acceptable?
                                        </Col>
                                        <Col lg={6}>
                                            <label style={{ marginRight: 15, fontWeight: 'normal' }}>
                                                <input
                                                    name="isDonorAcceptable"
                                                    type="radio"
                                                    style={{ marginRight: 5 }}
                                                    checked={this.state.isDonorAcceptable === true}
                                                    onClick={() => this.handleDonorIsAcceptable(true)}
                                                /> Yes
                                            </label>
                                            <label style={{ fontWeight: 'normal' }}>
                                                <input
                                                    name="isDonorAcceptable"
                                                    type="radio"
                                                    style={{ marginRight: 5 }}
                                                    checked={this.state.isDonorAcceptable === false}
                                                    onClick={() => this.handleDonorIsAcceptable(false)}
                                                /> No
                                            </label>
                                        </Col>
                                    </FormGroup>
                                </div>

                                {/* Reasons the target surgery date is unacceptable */}
                                {showDonorUnacceptableReasons && (
                                    <div className="row">
                                        <FormGroup validationState={this.state.declineValidation.donorUnacceptableReasons ? null : "error"}>
                                            <Col lg={6} componentClass={ControlLabel} style={{ paddingLeft: 35 }}>
                                                a. Reason(s) the donor is unacceptable. Check all that apply:
                                            </Col>
                                            <Col lg={6}>
                                                {donorIsUnacceptableReasonList.map((reason) => (
                                                    <div>
                                                        <div key={reason.id} style={{ display: 'inline-flex' }}>
                                                            <span>
                                                                <input
                                                                    type="checkbox"
                                                                    id={`donor-unacceptable-reason-${reason.id}`}
                                                                    checked={this.state.donorUnacceptableReasons.includes(reason.id)}
                                                                    onClick={() => this.handleDonorUnacceptableCheckboxChange(reason.id)}
                                                                    style={{ marginRight: '8px' }}
                                                                />
                                                            </span>
                                                            <span style={{ margin: '2px' }}>
                                                                <label style={{ fontWeight: 'normal', marginBottom: 0 }} htmlFor={`donor-unacceptable-reason-${reason.id}`}>
                                                                    {reason.description}
                                                                </label>
                                                            </span>
                                                            {reason.allowOtherText && reason.id != MatchOfferDeclineReason.PublicHealthServicePHSIncreasedRisk &&
                                                                <span style={{ marginLeft: '5px' }}>
                                                                    <FormGroup validationState={this.state.declineValidation.otherReasonDonorBad ? null : "error"}>
                                                                        <FormControl
                                                                            style={{ height: '90%', maxHeight: '25px' }}
                                                                            type="text"
                                                                            name="otherReasonDonorBad"
                                                                            placeholder=""
                                                                            value={this.state.otherReasonDonorBad}
                                                                            maxLength={50}
                                                                            onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => this.setState({ otherReasonDonorBad: e.currentTarget.value })}
                                                                            disabled={!this.state.donorUnacceptableReasons.includes(MatchOfferDeclineReason.DonorOther)}
                                                                        />
                                                                    </FormGroup>
                                                                </span>
                                                            }
                                                        </div>
                                                        {reason.id == MatchOfferDeclineReason.PublicHealthServicePHSIncreasedRisk
                                                            && this.state.donorUnacceptableReasons.includes(MatchOfferDeclineReason.PublicHealthServicePHSIncreasedRisk) &&
                                                            <div style={{ marginLeft: '24px' }}>
                                                                <FormGroup style={{ marginBottom: '6px' }} validationState={this.state.declineValidation.otherReasonPhs ? null : "error"}>
                                                                    <FormControl
                                                                        style={{ height: '90%', maxHeight: '25px', width: '235px' }}
                                                                        type="text"
                                                                        name="otherReasonPhs"
                                                                        placeholder=""
                                                                        value={this.state.otherReasonPhs}
                                                                        maxLength={150}
                                                                        onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => this.setState({ otherReasonPhs: e.currentTarget.value })}
                                                                    />
                                                                </FormGroup>
                                                            </div>
                                                        }
                                                    </div>
                                                ))}
                                            </Col>
                                        </FormGroup>
                                    </div>
                                )}

                                {/* 2) Is the target surgery date acceptable? */}
                                {showIsTargetSurgeryDateQuestion && (
                                    <div className="row">
                                        <FormGroup validationState={this.state.declineValidation.isSurgeryDateAcceptable ? null : "error"}>
                                            <Col lg={6} componentClass={ControlLabel}>
                                                2. Is the target surgery date acceptable?
                                            </Col>
                                            <Col lg={6}>
                                                <label style={{ marginRight: 15, fontWeight: 'normal' }}>
                                                    <input
                                                        name="isSurgeryDateAcceptable"
                                                        type="radio"
                                                        style={{ marginRight: 5 }}
                                                        checked={this.state.isSurgeryDateAcceptable === true}
                                                        onClick={() => this.handleIsSurgeryDateAcceptable(true)}
                                                    /> Yes
                                                </label>
                                                <label style={{ fontWeight: 'normal' }}>
                                                    <input
                                                        name="isSurgeryDateAcceptable"
                                                        type="radio"
                                                        style={{ marginRight: 5 }}
                                                        checked={this.state.isSurgeryDateAcceptable === false}
                                                        onClick={() => this.handleIsSurgeryDateAcceptable(false)}
                                                    /> No
                                                </label>
                                            </Col>
                                        </FormGroup>
                                    </div>
                                )}

                                {/* Reasons the target surgery date is unacceptable */}
                                {showIsTargetSurgeryDateUnacceptableReasons && (<>
                                    <div className="row">
                                        <FormGroup validationState={this.state.declineValidation.reasonsTargeySurgeryUnacceptable ? null : "error"}>
                                            <Col lg={6} componentClass={ControlLabel} style={{ paddingLeft: 35 }}>
                                                a. Reason(s) the target surgery date is unacceptable. Check all that apply:
                                            </Col>
                                            <Col lg={6}>
                                                {targetSurgeryIsUnacceptableReasonList.map((reason) => (
                                                    <div>
                                                        <div key={reason.id} style={{ display: 'inline-flex' }}>
                                                            {(!pairedReasons.includes(reason.id) || this.props.recipient.patientTypeId != PatientType.Waitlist) && (<>
                                                                <span>
                                                                    <input
                                                                        type="checkbox"
                                                                        id={`surgery-date-unacceptable-reason-${reason.id}`}
                                                                        checked={this.state.reasonsTargeySurgeryUnacceptable.includes(reason.id)}
                                                                        onClick={() => this.handleUnacceptableSurgeryCheckboxChange(reason.id)}
                                                                        style={{ marginRight: '8px' }}
                                                                    />
                                                                </span>
                                                                <span style={{ margin: '2px' }}>
                                                                    <label style={{ fontWeight: 'normal', marginBottom: 0 }} htmlFor={`surgery-date-unacceptable-reason-${reason.id}`}>
                                                                        {reason.description}
                                                                    </label>
                                                                </span>
                                                            </>)}
                                                            {reason.allowOtherText &&
                                                                <span style={{ marginLeft: '5px' }}>
                                                                    <FormGroup validationState={this.state.declineValidation.otherReasonSurgeryDateBad ? null : "error"}>
                                                                        <FormControl
                                                                            style={{ height: '90%', maxHeight: '50px' }}
                                                                            type="text"
                                                                            name="otherReasonSurgeryDateBad"
                                                                            placeholder=""
                                                                            maxLength={50}
                                                                            value={this.state.otherReasonSurgeryDateBad}
                                                                            onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => this.setState({ otherReasonSurgeryDateBad: e.currentTarget.value })}
                                                                            disabled={!this.state.reasonsTargeySurgeryUnacceptable.includes(MatchOfferDeclineReason.SurgeryDateOther)}
                                                                        />
                                                                    </FormGroup>
                                                                </span>
                                                            }
                                                        </div>
                                                    </div>
                                                ))}
                                            </Col>
                                        </FormGroup>
                                    </div>

                                    {!showNextAvailableTargetSurgeryDates && (
                                        <div className="row">
                                            <FormGroup validationState={this.state.declineValidation.nextAvailTargetSurgeryDate ? null : "error"}>
                                                <Col lg={6} componentClass={ControlLabel} style={{ paddingLeft: 35 }}>
                                                    b. Alternative surgery dates (earlier or later):
                                                </Col>
                                                <Col lg={4}>
                                                    <FormControl
                                                        style={{ height: '90%', maxHeight: '50px' }}
                                                        type="text"
                                                        name="nextAvailTargetSurgeryDate"
                                                        placeholder=""
                                                        maxLength={50}
                                                        value={this.state.nextAvailTargetSurgeryDate}
                                                        onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => this.setState({ nextAvailTargetSurgeryDate: e.currentTarget.value })}
                                                        disabled={showNextAvailableTargetSurgeryDates}
                                                    />
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 12 }}>
                                                        <span style={{ color: '#c9372e' }}>Limited to 50 characters.</span>
                                                        <span>{this.state.nextAvailTargetSurgeryDate.length}/50</span>
                                                    </div>
                                                </Col>
                                            </FormGroup>
                                        </div>
                                    )}
                                </>
                                )}

                                {/* Does the patient need to be deactivated? */}
                                {showPatientNeedToBeDeactivated && (
                                    <div className="row">
                                        <FormGroup validationState={this.state.declineValidation.deactivatePatient ? null : "error"}>
                                            <Col lg={6} componentClass={ControlLabel}>
                                                {patientDeactivateQuestionLabel}
                                            </Col>
                                            <Col lg={6}>
                                                <label style={{ marginRight: 15, fontWeight: 'normal' }}>
                                                    <input
                                                        name="deactivatePatient"
                                                        type="radio"
                                                        style={{ marginRight: 5 }}
                                                        checked={this.state.deactivatePatient === true}
                                                        onClick={() => this.setState({ deactivatePatient: true })}
                                                    /> Yes
                                                </label>
                                                <label style={{ fontWeight: 'normal' }}>
                                                    <input
                                                        name="deactivatePatient"
                                                        type="radio"
                                                        style={{ marginRight: 5 }}
                                                        checked={this.state.deactivatePatient === false}
                                                        onClick={() => this.setState({ deactivatePatient: false })}
                                                    /> No
                                                </label>
                                            </Col>
                                        </FormGroup>
                                    </div>
                                )}

                                {/* Reasons the patient needs to be deactivated */}
                                {showPatientNeedToBeDeactivatedReasons && (
                                    <div className="row">
                                        <FormGroup validationState={this.state.declineValidation.patientDeactivatedReasons ? null : "error"}>
                                            <Col lg={6} componentClass={ControlLabel} style={{ paddingLeft: 35 }}>
                                                a. Reason(s) the patient needs to be deactivated. Check all that apply:
                                            </Col>
                                            <Col lg={6}>
                                                {patientDeactivationReasonList.map((reason) => (
                                                    <div>
                                                        <div key={reason.id} style={{ display: 'inline-flex' }}>
                                                            <span>
                                                                <input
                                                                    type="checkbox"
                                                                    id={`patient-deactivated-reason-${reason.id}`}
                                                                    checked={this.state.patientDeactivatedReasons.includes(reason.id)}
                                                                    onClick={() => this.handlePatientDeactivatedCheckboxChange(reason.id)}
                                                                    style={{ marginRight: '8px' }}
                                                                />
                                                            </span>
                                                            <span style={{ margin: '2px' }} >
                                                                <label style={{ fontWeight: 'normal', marginBottom: 0 }} htmlFor={`patient-deactivated-reason-${reason.id}`}>
                                                                    {reason.description}
                                                                </label>
                                                            </span>
                                                            {reason.allowOtherText &&
                                                                <span style={{ marginLeft: '5px' }}>
                                                                    <FormGroup validationState={this.state.declineValidation.otherReasonForPatientDeactivation ? null : "error"}>
                                                                        <FormControl
                                                                            style={{ height: '90%', maxHeight: '50px' }}
                                                                            type="text"
                                                                            name="otherReasonForPatientDeactivation"
                                                                            placeholder=""
                                                                            maxLength={50}
                                                                            value={this.state.otherReasonForPatientDeactivation}
                                                                            onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => this.setState({ otherReasonForPatientDeactivation: e.currentTarget.value })}
                                                                            disabled={!this.state.patientDeactivatedReasons.includes(MatchOfferDeclineReason.PatientDeactivateOther)}
                                                                        />
                                                                    </FormGroup>
                                                                </span>
                                                            }
                                                        </div>
                                                    </div>
                                                ))}
                                            </Col>
                                        </FormGroup>
                                    </div>
                                )}

                                {(this.state.reasonsTargeySurgeryUnacceptable.includes(MatchOfferDeclineReason.PairedDonorNotAvailable)
                                    || this.state.reasonsTargeySurgeryUnacceptable.includes(MatchOfferDeclineReason.PatientNotAvailable)) && !showPatientNeedToBeDeactivated && (
                                        <div className="row">
                                            <Col lg={12} componentClass={ControlLabel} style={{ paddingLeft: 35 }}>
                                                Since the <span style={{ color: '#c9372e' }}>{targetSurgeryIsUnacceptableReasonList
                                                    .filter(r => (r.id === MatchOfferDeclineReason.PairedDonorNotAvailable || r.id === MatchOfferDeclineReason.PatientNotAvailable)
                                                        && this.state.reasonsTargeySurgeryUnacceptable.includes(r.id))
                                                    .map(r => r.description)
                                                    .join(', ')}</span>, declining this offer will deactivate them from matching.
                                            </Col>
                                        </div>
                                    )}

                                {invalidMatchOfferDecline && (
                                    <div className='row'>
                                        <Alert bsStyle='danger'>
                                            Please select the appropriate decline reasons
                                        </Alert>
                                    </div>
                                )}
                            </>
                        }
                    </div>
                </MatchDecisionBodyWrapper>
                <Modal.Footer>
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <FormGroup style={{ display: 'flex', alignItems: 'center', marginBottom: 0, marginRight: 20 }} validationState={(this.state.dirty && !this.state.acceptValidation.initials) || (this.state.dirty && !this.state.declineValidation.initials) ? 'error' : null}>
                            <ControlLabel style={{ marginRight: 10, marginBottom: 0 }}>Your Initials <sup className='req'>*</sup></ControlLabel>
                            <input
                                className='form-control'
                                style={{ width: 50, lineHeight: '20px !important', padding: '4px 6px !important', marginRight: 10 }}
                                type='text'
                                maxLength={3}
                                value={this.state.initials}
                                onChange={this.handleInitialsChange}
                            />
                        </FormGroup>

                        <Button type='button' bsStyle={this.props.accept ? 'primary' : 'danger'} disabled={invalidMatchOfferDecline} onClick={this.handleSubmit}>{this.props.accept ? 'Accept Offer' : 'Confirm Decline'}</Button>
                        <Button type='button' onClick={this.props.onCancel}>Cancel</Button>
                    </div>
                </Modal.Footer>
            </Modal>
        );
    }
}

export default MatchOfferDecision;